import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { makeStyles } from '@material-ui/core/styles'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import DragHandle from '@mui/icons-material/DragHandle'
import Edit from '@mui/icons-material/Edit'
import { Droppable, Draggable } from 'react-beautiful-dnd'
import ApiService from '../../../common/ApiCalls'
import FieldEditDialog, { FIELD_TYPES, displayFieldType } from './FieldEditDialog'

const verticalMenuProps = {
  MenuListProps: {
    sx: {
      '&&': {
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: 'column',
      },
    },
  },
}

const verticalMenuItemSx = {
  '&&': {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-start',
    textAlign: 'left',
    width: '100%',
  },
}

const leftAlignedSelectSx = {
  '&& .MuiSelect-select': {
    textAlign: 'left',
  },
}

const useStyles = makeStyles((theme) => ({
  fieldContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  fieldName: {
    flexGrow: 1,
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'flex-end',
  },
}))

const toFieldPayload = (field) => ({
  label: field.label,
  description: field.description,
  field_type: field.field_type,
  config: field.config,
  archived_at: field.archived_at ?? null,
})

const filterExistingFields = (fields, { inputValue }) => {
  const words = inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean)
  return fields.filter((field) => {
    const label = (field.label || '').toLowerCase()
    return words.every((word) => label.includes(word))
  })
}

const FieldEditor = ({
  cardIndex,
  items,
  addField,
  deleteField,
  moveField,
  latestFieldChange,
  onFieldChanged,
}) => {
  const classes = useStyles()
  const fieldTypes = FIELD_TYPES.map((fieldType) => fieldType.value)

  const [openDialog, setOpenDialog] = useState(false)
  const [dialogStep, setDialogStep] = useState('choose')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [availableFields, setAvailableFields] = useState([])
  const [selectedFieldId, setSelectedFieldId] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [filterFieldType, setFilterFieldType] = useState('')

  useEffect(() => {
    fetchFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!latestFieldChange?.id) return

    setAvailableFields((previous) => {
      const fieldExists = previous.some(
        (field) => field.id === latestFieldChange.id
      )
      return fieldExists
        ? previous.map((field) =>
            field.id === latestFieldChange.id ? latestFieldChange : field
          )
        : [...previous, latestFieldChange]
    })
  }, [latestFieldChange])

  const fetchFields = async () => {
    try {
      const response = await ApiService().listFields()
      setAvailableFields((response || []).filter(field => !field.archived_at))
    } catch (error) {
      console.error('Error fetching fields:', error)
      setAvailableFields([])
    }
  }

  const closeAddFieldDialog = () => {
    setOpenDialog(false)
    setDialogStep('choose')
    setSelectedFieldId('')
    setSearchValue('')
    setFilterFieldType('')
  }

  const handleAddExistingField = () => {
    const selectedField = availableFields.find(
      (field) => field.id === selectedFieldId
    )
    if (!selectedField) return

    addField(cardIndex, {
      item_type: 'field',
      field_id: selectedField.id,
      display_label: selectedField.label,
      display_field_type: selectedField.field_type,
    })
    closeAddFieldDialog()
  }

  const handleCreateField = async (field) => {
    const createdField = await ApiService().createField(toFieldPayload(field))
    if (!createdField?.id) {
      throw new Error('Field creation did not return a field.')
    }

    setAvailableFields((previous) => [...previous, createdField])
    addField(cardIndex, {
      item_type: 'field',
      field_id: createdField.id,
      display_label: createdField.label,
      display_field_type: createdField.field_type,
    })
    onFieldChanged?.(createdField)
    return createdField
  }

  const handleUpdateField = async (field) => {
    const updatedField = await ApiService().updateField(
      field.id,
      toFieldPayload(field)
    )
    if (!updatedField?.id) {
      throw new Error('Field update did not return a field.')
    }

    setAvailableFields((previous) =>
      previous.map((candidate) =>
        candidate.id === updatedField.id ? updatedField : candidate
      )
    )
    onFieldChanged?.(updatedField)
    return updatedField
  }

  const handleEditField = async (item) => {
    let field = availableFields.find(
      (candidate) => candidate.id === item.field_id
    )

    if (!field) {
      const fetchedFields = await ApiService().getFields(item.field_id)
      field = fetchedFields?.[0]
    }

    if (!field) {
      console.error('Unable to load field ' + item.field_id + ' for editing.')
      return
    }

    setEditingField(field)
  }

  const filteredFields = availableFields.filter(
    (field) => !filterFieldType || field.field_type === filterFieldType
  )
  const selectedField =
    availableFields.find((field) => field.id === selectedFieldId) || null

  return (
    <div>
      <Button
        variant='contained'
        onClick={() => {
          setOpenDialog(true)
          setDialogStep('choose')
        }}
        startIcon={<Add />}
        style={{ marginTop: 8 }}
      >
        Add Field
      </Button>

      <Droppable
        droppableId={'fields-' + cardIndex}
        type={'FIELD-' + cardIndex}
      >
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {items.map((item, index) => (
              <Draggable
                key={index}
                draggableId={'field-' + cardIndex + '-' + index}
                index={index}
              >
                {(provided) => (
                  <div
                    className={classes.fieldContainer}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                  >
                    <div {...provided.dragHandleProps}>
                      <DragHandle />
                    </div>
                    <div className={classes.fieldName}>
                      {item.display_label
                        ? item.display_label +
                          ' (' +
                          displayFieldType(item.display_field_type) +
                          ')'
                        : 'Field'}
                    </div>
                    <IconButton
                      aria-label={
                        'edit field ' + (item.display_label || '')
                      }
                      onClick={() => handleEditField(item)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      aria-label={
                        'remove field ' + (item.display_label || '')
                      }
                      onClick={() => deleteField(cardIndex, index)}
                    >
                      <Delete />
                    </IconButton>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      <Dialog
        open={openDialog}
        onClose={closeAddFieldDialog}
        maxWidth='sm'
        fullWidth
      >
        {dialogStep === 'choose' && (
          <>
            <DialogTitle>Add Field</DialogTitle>
            <DialogContent>
              <List
                sx={{
                  '&&': {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    padding: 1,
                  },
                }}
              >
                {availableFields.length > 0 && (
                  <ListItemButton
                    sx={verticalMenuItemSx}
                    onClick={() => setDialogStep('select')}
                  >
                    <ListItemIcon>
                      <Add />
                    </ListItemIcon>
                    <ListItemText primary='Select Existing Field' />
                  </ListItemButton>
                )}
                <ListItemButton
                  sx={verticalMenuItemSx}
                  onClick={() => {
                    closeAddFieldDialog()
                    setCreateDialogOpen(true)
                  }}
                >
                  <ListItemIcon>
                    <Add />
                  </ListItemIcon>
                  <ListItemText primary='Create New Field' />
                </ListItemButton>
              </List>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeAddFieldDialog}>Cancel</Button>
            </DialogActions>
          </>
        )}

        {dialogStep === 'select' && availableFields.length > 0 && (
          <>
            <DialogTitle>Select Existing Field</DialogTitle>
            <DialogContent>
              <div className={classes.searchContainer}>
                <Autocomplete
                  autoHighlight
                  openOnFocus
                  options={filteredFields}
                  filterOptions={filterExistingFields}
                  value={selectedField}
                  inputValue={searchValue}
                  getOptionLabel={(field) =>
                    field.label + ' (' + displayFieldType(field.field_type) + ')'
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, field) => (
                    <li {...props} key={field.id}>
                      {field.label + ' (' + displayFieldType(field.field_type) + ')'}
                    </li>
                  )}
                  noOptionsText='No matching fields'
                  onChange={(_, field) => {
                    setSelectedFieldId(field?.id || '')
                  }}
                  onInputChange={(_, value, reason) => {
                    setSearchValue(value)
                    if (reason === 'input') setSelectedFieldId('')
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Search Fields'
                      placeholder='Start typing to find a field'
                      variant='outlined'
                    />
                  )}
                  style={{ flex: 1 }}
                />
                <FormControl
                  variant='outlined'
                  sx={{ flex: '0 0 220px', width: 220 }}
                >
                  <InputLabel id='filter-field-type-label'>
                    Filter by Type
                  </InputLabel>
                  <Select
                    labelId='filter-field-type-label'
                    id='filter-field-type'
                    label='Filter by Type'
                    value={filterFieldType}
                    onChange={(event) => setFilterFieldType(event.target.value)}
                    MenuProps={verticalMenuProps}
                    sx={leftAlignedSelectSx}
                  >
                    <MenuItem sx={verticalMenuItemSx} value=''>
                      All Types
                    </MenuItem>
                    {fieldTypes.map((type) => (
                      <MenuItem
                        sx={verticalMenuItemSx}
                        key={type}
                        value={type}
                      >
                        {displayFieldType(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogStep('choose')}>Back</Button>
              <Button
                onClick={handleAddExistingField}
                color='primary'
                disabled={!selectedFieldId}
              >
                Add Field
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {createDialogOpen && (
        <FieldEditDialog
          open
          field={null}
          existingFields={availableFields}
          mode='create'
          onClose={() => setCreateDialogOpen(false)}
          onSave={handleCreateField}
        />
      )}

      {editingField && (
        <FieldEditDialog
          open
          field={editingField}
          existingFields={availableFields}
          mode='edit'
          onClose={() => setEditingField(null)}
          onSave={handleUpdateField}
        />
      )}
    </div>
  )
}

export default FieldEditor
